import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { apiGet, apiDelete, apiPost, apiPut } from '../../generic/Api_Functions'
import moment from 'moment/moment'
import { Box, Button, Chip, CircularProgress, Grid, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { Balance, UploadFileRounded, ManageSearchOutlined, PauseCircle, PlayCircleFilledOutlined, Schema, StorageRounded, TextSnippetOutlined, HourglassTopOutlined, EditNote, WarningAmber } from '@mui/icons-material'
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ToggleButton from '@mui/material/ToggleButton'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SensitiveBoundary from '../../vision/SensitiveBoundary'
import { useVision } from '../../vision/visionContext'
import { useProtectedRowPlaceholder, useVisionCoversSensitive } from '../../vision/protectedRow'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { enumFilterOptions, systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import AlertUI from '../../generic/AlertUI'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import DeleteIcon from '@mui/icons-material/Delete'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import LinkIcon from '@mui/icons-material/Link'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useToggle } from '../../generic/hooks/useToggle'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import ActionSelector from '../../generic/ActionSelector'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import useFabricationOrderClusterSendSetting from '../../generic/hooks/useFabricationOrderClusterSendSetting'
import useFabricationOrderAlignmentSetting from '../../generic/hooks/useFabricationOrderAlignmentSetting'
import SyncIcon from '@mui/icons-material/Sync'
import { alignmentCellText, hasAlignmentIndicator } from './fabricationOrderAlignmentCell'
import FabricationOrderResyncAction from './FabricationOrderResyncAction'
import InfoIcon from '@mui/icons-material/Info'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import PriorityConditionChecker from '../../generic/utilities/PriorityConditionChecker'
import MainImageThumbnail, { COLUMN_WIDTH as MAIN_IMAGE_COLUMN_WIDTH } from '../parts/MainImageThumbnail'
import useMainImageResolution from '../parts/useMainImageResolution'
import { canBulkTransitionFabricationOrderSelection, isFabricationOrderOperable } from '../../generic/utilities/statusPermissionGating'
import ForcedReturnDialog from './ForcedReturnDialog'
import FabricationOrderClusterSendAction from './FabricationOrderClusterSendAction'
import { useForcedReturnGate } from './useForcedReturnGate'
import FabricationOrderDetailDialog from './detail/FabricationOrderDetailDialog'
import { fabricationOrderSaveErrorMessage, isNoMachineForTechnologyError } from './fabricationOrderSaveError'
// H179 · EM-5: EL campo Workflow de la ficha, el mismo componente, para la acción de fila de la ventana antigua.
import FabricationOrderWorkflowField from './detail/FabricationOrderWorkflowField'
// Historia 137 (de develop): la alerta agregada al rechazar una vuelta a PENDING. Se usa en el
// manejador de error del CAMBIO DE ESTADO, que es COMPARTIDO por las dos ventanas — ver ahí.
import { buildActiveJobsConflictMessage, isActiveJobsConflict } from './activeJobsConflictAlert'
import { describeWorkflow, isWorkflowOriginKnown } from '../../generic/utilities/workflowOrigin'
import { copyBodyOf, creationBodyOf, isBulkStatusBlockedByWithoutWorkflow, rowHasWorkflow, workflowOptionsWithSentinel } from './withoutWorkflowOption'
import useRepositorySelection from '../../generic/hooks/useRepositorySelection'
import { parentPreselectedRecordFrom } from '../../generic/utilities/repositorySelectionMapping'
import { statusProgressLabel } from './statusProgressLabel'
import ImportConnectorDialog from './importConnector/ImportConnectorDialog'
import { shouldUseImportConnector } from './importConnector/connectorImportMode'

// ⚠️ ADDENDUM 14 (E20) — LO MARCADO ASÍ ES LA VENTANA ANTIGUA.
// El interruptor `USE_FABRICATION_ORDER_DETAIL` decide cuál de las dos se usa, y apagado gana la
// antigua. Todo este bloque es lo que T7 retiró (d006bd395) y vuelve VERBATIM: cada línea que se
// reescribiera al traerla es una línea que puede derivar, y el contrato exige que apagado se
// comporte como se comportaría hoy si esta historia no lo hubiera tocado.
// PARA RETIRARLO EL DÍA QUE SE QUITE EL INTERRUPTOR: borrar los bloques marcados con este aviso.
import ModifyQuantityDialog from './ModifyQuantityDialog'
import FabricationOrderPartProgressDialog from './FabricationOrderPartProgressDialog'
import PausePartSelectEndJobDialog from '../parts/pause/PausePartSelectEndJobDialog'
import PauseResumeFinishedList from '../parts/pause/PauseResumeFinishedList'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../generic/qr/QrCodeGeneratorDialog'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { DateTimePicker } from '@mui/x-date-pickers'
import { WorkflowHierarchyProvider } from '../workflow2.0/WorkflowRecursionDetector'
import { alertForSaveError } from '../workflow2.0/externalReturnSaveError'
import { prepareFabricationOrderWorkflowForSave } from '../workflow2.0/feature/featureGroupRevisionMode'
import { withExplicitNightJob } from '../workflow2.0/jobNightCharacter'
import { clearTempDocumentScopes, collectTempDocumentUUIDs } from '../workflow2.0/tempDocumentScopes'
import { diffObjects } from '../../generic/utilities/ObjectDiffUtil/objectDiffUtil'
import { findNewExternalJobMissingReturnTiming, updatePositions } from '../workflow2.0/utils'
import { isDuplicatePlanningGroupSaveError, extractDuplicatePlanningGroupValues, DUPLICATE_PLANNING_GROUP_MESSAGE_KEY } from '../workflow2.0/planningGroupSaveError'
import { partResultLabel } from '../../generic/utilities/partResultLabel'
import { useNodesState } from 'reactflow'
import useFabricationOrderDetailSetting from '../../generic/hooks/useFabricationOrderDetailSetting'

// ⚠️ VENTANA ANTIGUA (E20) — las tres pantallas pesadas de sus diálogos van PEREZOSAS, por el mismo
// motivo que las del detalle: `Workflow` y `Balanced` arrastran `reactflow`, y `Document` llega a
// `three` (SmartDocumentViewer -> SmartThreejs). Importadas arriba, el listado de OFs pagaría un
// motor 3D y un lienzo de nodos en CADA carga, con el interruptor encendido o apagado, sin que nadie
// abra ninguno de los tres. Solo se piden cuando su diálogo se abre — que ya era la condición.
const Workflow = lazy(() => import('../workflow2.0/Workflow'))
const Balanced = lazy(() => import('../balanced/Balanced'))
const Document = lazy(() => import('../document/Document'))

const LegacyDialogFallback = () => (
  <Stack alignItems='center' justifyContent='center' sx={{ minHeight: 240 }}>
    <CircularProgress />
  </Stack>
)

// Import typedefs from another file
/**
 *  * @typedef {import('../../generic/utilities/objectStructures/orderWindow/order').Order} Order Order object
 *  * @typedef {import('../../generic/utilities/objectStructures/orderWindow/lineOrder').LineOrder} LineOrder LineOrder object
 *  * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
 *  * @typedef {import('../../generic/utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn} ExtendedColumn ExtendedColumn object
 *  * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 */

// H187 D6 (S44-S48): compact cell: ONLY the copy code, plus a small icon when the workflow is a copy (it carries
// an `origin`). The origin text comes untouched from the single formatter and goes in the icon's tooltip and
// aria-label; this cell composes no origin text. The native `title` carries only the code (the cell is `noWrap`, so it
// is the full code when truncated) and sits on the code span, not on the icon, so it never doubles the tooltip.
const WorkflowCodeAndOrigin = ({ workflow }) => {
  const { t_ } = useSmartTranslation()
  const { code, originText } = describeWorkflow(workflow, t_)
  const isKnown = isWorkflowOriginKnown(workflow?.origin)
  const OriginIcon = isKnown ? LinkIcon : HelpOutlineIcon
  return (
    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
      <Typography component='span' variant='inherit' noWrap title={code}>{code}</Typography>
      {originText && (
        <Tooltip title={originText}>
          <OriginIcon fontSize='inherit' color={isKnown ? 'inherit' : 'disabled'} role='img' aria-hidden={false} aria-label={originText} />
        </Tooltip>
      )}
    </Box>
  )
}

/**
 * RN1/RN3/RN4 (#30): a Fabrication Order row is "late vs. its target" only when both
 * plannedEndDate and targetDate are present, parseable, and plannedEndDate is strictly
 * after targetDate (exact timestamp comparison). Missing, equal or non-parseable values
 * are fail-safe: they never mark the row as late (RN4 + fail-safe validation).
 *
 * @param {string|Date} plannedEndDate Planned end date (read-only, may be absent)
 * @param {string|Date} targetDate Target date (mandatory)
 * @returns {boolean} true only when plannedEndDate > targetDate
 */
const isPlannedEndDateLate = (plannedEndDate, targetDate) => {
  if (!plannedEndDate || !targetDate) {
    return false
  }
  const planned = moment(plannedEndDate, moment.ISO_8601, true)
  const target = moment(targetDate, moment.ISO_8601, true)
  if (!planned.isValid() || !target.isValid()) {
    return false
  }
  return planned.isAfter(target)
}

/**
 *
 * @param {Object} props Props Object
 * @param {Order} props.order Props Object
 * @param {LineOrder} props.lineOrder Line Order object
 * @param {IdCodeName} props.client Client Object
 * @param {boolean} props.addModeByDefault Add mode by default
 *
 * @returns
 */
const FabricationOrder = ({ order, lineOrder, client, addModeByDefault, allFoMode }) => {
  const [AlertElement, showAlert] = AlertUI()
  // T7 retired the row's own edit mode (S272) and with it every use of `editing` from this file;
  // the merge of develop brings it back into use: the resync action of historia 108 gates on it
  // (`disabled: editing || ...`), so the value is restored with its original meaning — the table
  // still turns it on while a row is being edited/added, and that gate has to keep working.
  const [editing, setEditing] = useState(false)
  const { t_ } = useSmartTranslation()
  // `format` lo usa el formulario de la ventana antigua (E20); `formatDateTime`, las dos ventanas.
  const { format, formatDateTime } = useDateFormatter()
  const [showDialog, setShowDialog] = useState({ open: false, rowValues: null })
  const [isAddingNewRow, setIsAddingNewRow] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const isUserAuthorized = useAuthorization()
  // Historia 170 (J4b, S89): FABRICATION_ORDER has no child window in the Gherkin contract. This
  // component is ALSO used nested (e.g. a fabrication order's own sub-tables) — `useRepositorySelection`
  // only ever matches a row when THIS is the `/fabricationOrders` route instance and the id is
  // actually present in its own dataset; every other mounted instance simply resolves "not found"
  // (harmless, same as any other absent match — S100/S101).
  const repositorySelection = useRepositorySelection()
  // S69 (develop, historia 108): the row the user asked to resynchronise. One at a time, by design
  // (P2-D4). Llega con el merge de develop y se conserva: es funcionalidad de otra historia, ajena
  // al reparto de acciones de T7.
  const [resyncRow, setResyncRow] = useState({})
  const [resetMultipleSelectionValue, toogleResetMultipleSelectionValue] = useToggle()
  const [allValue, toggleAllValue] = useToggle()
  // H179 (RN6/C4): "solo OF sin workflow". Lo filtra Sun (`withoutWorkflow=true` junto a la
  // paginación), no el navegador: una página filtrada en cliente mentiría sobre el total.
  // H179 EM-6 (RN6-bis): el botón de la barra muestra u oculta las OF sin workflow. Sale ACTIVADO: por defecto
  // se ven también las OF sin workflow, y al soltarlo se ocultan.
  const [showWithoutWorkflow, setShowWithoutWorkflow] = useState(true)
  // H179 EM-6-bis (S145): la decisión de ocultarlas la comparten el listado y las acciones masivas, para que
  // "seleccionar todo" actúe solo sobre lo que se ve. Solo en `allFoMode`; el listado de una línea no la lleva.
  const hideWithoutWorkflow = allFoMode && !showWithoutWorkflow
  const withoutWorkflowQuery = hideWithoutWorkflow ? '?withoutWorkflow=false' : ''
  // T7 (S270/S271/RN71): the row keeps only "Open detail" (and Borrar) — everything else that used
  // to be its own dialog/state here now lives inside the detail (`FabricationOrderDetailDialog`).
  const [detailDialog, setDetailDialog] = useState({ open: false, fabricationOrderId: null, sensitive: false })
  // Story 183 (S29 · RN10): a successful workflow save makes Sun rewrite the FO's derived "noche"
  // column, so the table reloads its current page (`forceLoad`) to show it without reloading the page.
  const [tableReloadSignal, setTableReloadSignal] = useState(0)
  const reloadTable = useCallback(() => setTableReloadSignal((signal) => signal + 1), [])

  // ⚠️ VENTANA ANTIGUA (E20) — estado de la edición en línea y de las nueve acciones de fila.
  // Se declara SIEMPRE, y no bajo el interruptor, porque un hook no puede ser condicional: lo que
  // decide el interruptor es qué se OFRECE y qué se PINTA, no qué estado existe.
  const [AlertElementDialog, showAlertDialog] = AlertUI()
  const [currentFabricationOrder, setCurrentFabricationOrder] = useState(null)
  const [openPartsDialog, setOpenPartsDialog] = useState(false)
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
  const [openProgressDialog, setOpenProgressDialog] = useState(false)
  const [confirmationAction, setConfirmationAction] = useState('')
  const [rowIdToConfirm, setRowIdToConfirm] = useState(null)
  const [openCopyDialog, setOpenCopyDialog] = useState(false)
  const [unfinishedPartsPauseResume, setUnfinishedPartsPauseResume] = useState([])
  const [partsOnDevicesToPause, setPartsOnDevicesToPause] = useState([])
  const [ofCopy, setOfCopy] = useState(null)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [formData, setFormData] = useState({
    fabricationOrder: '',
    quantity: 1,
    targetDate: formatDateTime(moment()),
    priority: 5,
    targetDateError: false
  })
  const [showDocument, setShowDocument] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [qrRow, setQrRow] = useState(null)
  const [fabricationOrderRow, setFabricationOrderRow] = useState(null)
  const [selectedWorkflowFabricationOrderId, setSelectedWorkflowFabricationOrderId] = useState()
  // S31 (RN17/D7): status of the FabricationOrder whose workflow editor is being opened, used to
  // gate Skip/Reactivate on a closed/cancelled FO.
  const [selectedWorkflowFabricationOrderStatus, setSelectedWorkflowFabricationOrderStatus] = useState('')
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false)
  // RN117.2: whether the workflow editor inside the window would accept a save right now.
  const [workflowSaveAllowed, setWorkflowSaveAllowed] = useState(true)
  // Saving a large workflow takes seconds; a second Accept meanwhile sent a concurrent PUT that Sun
  // rejected ("Error saving workflow") after the first had already saved. Same pair as Workflow.jsx:
  // the ref is the synchronous guard (a click before repaint already sees it), the state disables Accept.
  const savingWorkflowRef = useRef(false)
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false)
  // H145 (RN1/RN3/CA1/CA2): blocking loading indicator for the workflow editor, shown from the
  // click to the outcome. Also guards the row action itself (see the 'Workflow' onClick below):
  // DI2 relies on the modal backdrop to keep a second click from reaching the table, but a
  // defensive in-code guard costs one line and makes the "one load at a time" invariant hold
  // even when the action is invoked directly (not through a real pointer click).
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false)
  // H179 · EM-5: con la ficha APAGADA, el workflow de una OF existente se asigna, cambia o quita desde
  // «Editar» de la fila: la celda Workflow monta EL MISMO campo que el bloque General de la ficha (C1, candado
  // y motivos, confirmación, C2 y rechazos). Va por su propia puerta (C2), no por el guardado de la fila, y la
  // tabla se recarga al guardar o cancelar la edición. Solo con el permiso de C2.
  const canManageWorkflow = isUserAuthorized('ORDERS') && isUserAuthorized('FABRICATION ORDERS')
  const handleLegacyWorkflowAssigned = useCallback(() => {
    showAlert({ severity: 'success', title: t_('Workflow updated correctly'), message: '' })
  }, [showAlert, t_])
  const [currentWorkflow, setCurrentWorkflow] = useState()
  const [currentOriginalWorkflow, setCurrentOriginalWorkflow] = useState()
  const [selectedBalancedRow, setSelectedBalancedRow] = useState({})
  const [balancedWorkflow, setBalancedWorkflow] = useState({})
  const [showBalancedDialog, setShowBalancedDialog] = useState(false)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [allowEdit, setAllowEdit] = useState(false)
  // S22-S26 / S32: "Modify quantity" modal (form + confirmation + progress) lives in its own
  // component (ModifyQuantityDialog). Here we only track which row it is open for.
  const [modifyQuantityDialog, setModifyQuantityDialog] = useState({ open: false, row: null })
  const balancedSaveRef = useRef(() => {})
  const iconToDisplay = confirmationAction === 'pause' ? PauseCircle : PlayCircleFilledOutlined
  // ⚠️ FIN del estado de la ventana antigua.
  // S36: the generic table reduces every selected row to its primary keys only — `{ id: '5' }`, and
  // stringified (SmartMateriaUITable.jsx, reduceRowPropertiesToDesired) — so the selection payload
  // carries no `status`. Index the status of every fetched FO by id so the per-destination gating
  // can be evaluated against the real current statuses instead of an always-undefined one.
  const statusByFabricationOrderId = useRef({})
  // The same for "has no workflow": a fabrication order without workflow is never launched and, once CLOSED,
  // never returns to PENDING, so those bulk destinations are disabled for a selection that contains one.
  const withoutWorkflowByFabricationOrderId = useRef({})
  // ⚠️ ADDENDUM 14 (E20): el interruptor de la ventana. Apagado —y también si no se puede leer—
  // manda la ANTIGUA: edición en línea y las nueve acciones de fila. Encendido, "Abrir detalle".
  const detailWindowEnabled = useFabricationOrderDetailSetting()
  // Story 213 v2 (O-J3, PO 20-10-2026): the old detail window (Workflow, Balanced, Documents) is protected like the new
  // detail: a boundary when the row was listed with `sensitive: true` or the screen FABRICATION_ORDER_DETAIL is marked.
  // It goes INSIDE each dialog's root element, so the element each dialog receives keeps its type.
  const { markedScreens } = useVision()
  // Story 213 S302 (C15.6): while it covers, the whole row of a protected fabrication order is the blurred placeholder.
  const protectedRowPlaceholder = useProtectedRowPlaceholder()
  const visionCoversSensitive = useVisionCoversSensitive()
  const [legacyRowSensitive, setLegacyRowSensitive] = useState(false)
  const legacyWindowSensitive = legacyRowSensitive || markedScreens.includes('FABRICATION_ORDER_DETAIL')
  const [frontendConfig] = useFrontEndConfig()
  // Story 194 (S27-S29, S48): with the connector on, the general list gets its own import button and screen.
  const importViaConnector = shouldUseImportConnector({ allFoMode, frontendConfig })
  const [importConnectorOpen, setImportConnectorOpen] = useState(false)
  // S34/S38/S39/S54 (historia 107): the cluster send is offered only when the installation is in a
  // cluster (RN1), the enabling setting is active (RN1.1, default OFF) and the profile owns the
  // specific permission (RN33/DG-12). Anything else leaves the window exactly as it is today.
  //
  // The flag is not a field of `frontEndConfig` but the cluster send switch of Sun
  // (`cluster/fabricationOrderSendSetting`), administered from the maintenance cluster screen.
  // `frontEndConfig` still owns `inCluster`, and it GUARDS the read: out of a cluster the switch is
  // not even requested (S39/CA10).
  const inCluster = frontendConfig?.inCluster || false
  const clusterSendSettingEnabled = useFabricationOrderClusterSendSetting(inCluster)
  const clusterFabricationOrderSyncEnabled = inCluster && clusterSendSettingEnabled
  // S71 (historia 108, P2-DG-28): the indicator hangs off the ALIGNMENT switch, which is a
  // different question from part 1's send switch and is read from its own route. A node that does
  // not emit alignment shows no indicator -- it could not maintain one -- even with the send on.
  const alignmentSettingEnabled = useFabricationOrderAlignmentSetting(inCluster)
  const clusterAlignmentEnabled = inCluster && alignmentSettingEnabled
  // S69/S102: the resync PUSHES this node's version outwards, so it asks for part 1's very same
  // permission (RN40/V16, and `FabricationOrderAlignmentRest:78` gates it exactly so).
  const clusterResyncEnabled = clusterAlignmentEnabled && isUserAuthorized('FABRICATION_ORDER_CLUSTER_SEND')
  const clusterSendEnabled = clusterFabricationOrderSyncEnabled && isUserAuthorized('FABRICATION_ORDER_CLUSTER_SEND')
  const [clusterTargetNodes, setClusterTargetNodes] = useState([])
  const extendedColumns = useGetExtendedColumns('fabrication_order')
  // Main image is resolved per visible page from the effective part reference id (S49 / D6),
  // never from data copied into the OF. Re-resolving on each fetch reflects the current image
  // after a refresh (S51 / S55).
  const [mainImageInfoMap, resolveMainImages, mergeMainImages, mainImageThumbnailMap] = useMainImageResolution()

  // S32 (CU6/RN21, historia #49): loading indicator + duplicate-submit block while the
  // create/edit request is in flight (the JIT delivery-feasibility check can take seconds).
  const [isSavingFabricationOrder, setIsSavingFabricationOrder] = useState(false)
  // S33-S35 (CU7/RN14,RN15,RN22): the "requires confirmation" dialog — open + the dates to
  // display (targetDate/suggestedDate) or the custom processor text, plus the pending
  // mode/row/suggestedDate needed to retry on accept (S34). suggestedDate is kept EXACTLY as
  // received (raw ISO) because both the display and the S34 retry reuse it (RN15 caveat).
  const [deliveryConfirmationDialog, setDeliveryConfirmationDialog] = useState({
    open: false, customMessage: '', targetDate: '', suggestedDate: '', mode: 'create', row: null
  })

  // S119-S122 (CU6/RN44, historia 100): the forced-return dialog gate wraps ONLY the cancel
  // (delete) and close (status -> CLOSED) operations — never PENDING/LAUNCHED, which are not
  // "cancel or close" and Sun's own gate (plan-sun.md, Lote 11/16) never touches either. When Sun
  // has nothing to force-return, `runGuarded` is a transparent pass-through (S122: no new dialog,
  // no extra step, identical success/error handling).
  const forcedReturnGate = useForcedReturnGate()
  const handleForcedReturnConfirm = (decisions) => forcedReturnGate.onConfirm(decisions)
  const handleForcedReturnCancel = () => forcedReturnGate.onCancel()

  // Effective part reference of an OF: its own reference, falling back to the line order's (D6,
  // matching the backend). Used to resolve the main image for both direct and inherited references.
  const effectivePartReferenceId = useCallback((row) => row?.partReference?.id || lineOrder?.partReference?.id, [lineOrder])

  // S34/DG-13: the selector offers every node of the cluster, not the user's nodes, minus the
  // current one (DG-18). `cluster/getNodeList` answers OBJECTS (`{ id, name }`) -- the same source
  // the maintenance cluster screen reads -- while the current node arrives as a message; both are
  // compared by NAME, which is also what travels as `targetNodeName` in the send. The calls are
  // made ONLY when the feature is enabled, so an installation out of the cluster (or with the
  // switch off) issues exactly the same requests as before this historia (S39/S54).
  useEffect(() => {
    if (!clusterSendEnabled && !clusterResyncEnabled) {
      return
    }
    const loadClusterTargetNodes = async () => {
      const clusterNodes = await apiGet('cluster/getNodeList')
      const currentNode = await apiGet('cluster/getNodeName')
      const currentNodeName = currentNode?.message || ''
      setClusterTargetNodes((clusterNodes || [])
        .map((node) => node?.name || '')
        .filter((nodeName) => nodeName !== '' && nodeName !== currentNodeName))
    }
    loadClusterTargetNodes()
  }, [clusterSendEnabled, clusterResyncEnabled])

  const toolbar = {
    render: {
      fetchFunction: () => {
        return (
          <>
            <ToggleButton
              selected={allValue}
              onChange={() => toggleAllValue()}
              color='success'
              value='ALL'
              aria-label='ALL'
            >
              {t_('SHOW ALL')}
            </ToggleButton>
            {/* H179: C4 solo existe en los dos listados generales; el de una línea de pedido no lo
                recibe, así que ahí el botón no se ofrece. EM-6 (S139): un botón como SHOW ALL que muestra
                u oculta las OF sin workflow, con el texto corto del chip y el largo de tooltip y nombre accesible. */}
            {allFoMode && (
              <Tooltip title={t_('Show fabrication orders without workflow')}>
                <ToggleButton
                  selected={showWithoutWorkflow}
                  onChange={() => setShowWithoutWorkflow((previous) => !previous)}
                  color='success'
                  value='WITHOUT_WORKFLOW'
                  aria-label={t_('Show fabrication orders without workflow')}
                >
                  {t_('Without workflow')}
                </ToggleButton>
              </Tooltip>
            )}
          </>
        )
      }
    },
    ...(importViaConnector
      ? {
          // Same look and text as the classic button, which the table hides when it receives no `importFile`.
          renderSticky: {
            fetchFunction: () => (
              <Button
                sx={{ borderColor: 'transparent' }}
                color='primary'
                variant='outlined'
                onClick={() => setImportConnectorOpen(true)}
                disabled={!isUserAuthorized('IMPORT_FILE_FABRICATION_ORDERS')}
              >
                <UploadFileRounded sx={{ fontSize: '1.2em' }} />
                <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('IMPORT FILE')}</Typography>
              </Button>
            )
          }
        }
      : {})
  }

  const columns = useMemo(() => [
    {
      name: t_('Fabrication Order'),
      field: 'fabricationOrder',
      charLimit: 60,
      mandatory: true,
      unique: true,
      readOnly: true
    },
    {
      name: t_('Main image'),
      field: 'mainImage',
      width: MAIN_IMAGE_COLUMN_WIDTH,
      readOnly: true,
      disableFilter: true,
      search: false,
      render: (row) => {
        const partReferenceId = effectivePartReferenceId(row)
        if (partReferenceId === undefined || partReferenceId === null) {
          return null
        }
        return (
          <MainImageThumbnail
            partReferenceId={partReferenceId}
            mainImageInfo={mainImageInfoMap[partReferenceId] || {}}
            thumbnail={mainImageThumbnailMap[partReferenceId] || ''}
          />
        )
      }
    },
    {
      name: '',
      tuneLabel: t_('Comment'),
      field: 'partReference.comment',
      search: false,
      sortable: false,
      render: (row) => {
        if (!row.partReference?.comment) {
          return null
        }
        return (
          <Tooltip title={<Typography>{row.partReference?.comment}</Typography>}>
            <Typography><InfoIcon sx={{ marginBottom: editingItem && editingItem['smart-table-index'] === row['smart-table-index'] ? '1.25rem' : 0 }} /></Typography>
          </Tooltip>
        )
      }
    },
    {
      name: t_('Part Reference'),
      field: 'partReference.code',
      mandatory: allFoMode,
      type: 'smartSelect',
      hide: !allFoMode,
      readOnly: true,
      onChange: (changeValue) => {
        if (editingItem) { editingItem.workflow = null }
        // Resolve the freshly picked reference's main image so the thumbnail previews immediately,
        // before saving (D6 / S49). Merge so the rest of the page's thumbnails are kept.
        mergeMainImages([changeValue?.partReference?.id])
      },
      edit: {
        source: async () => {
          return await apiGet('partReferencesShortData')
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Workflow'),
      field: 'workflow.name',
      // Sin elegir nada en el alta, Sun asigna el workflow por defecto de la Part Reference (y, si no
      // tiene, la crea sin workflow): ver `creationBodyOf`.
      mandatory: false,
      type: 'smartSelect',
      readOnly: true,
      // H179 (RN9/D3): "Without workflow" va SIEMPRE primero y nada se preselecciona. El selector
      // ordenaría por su cuenta —y mezclaría la centinela con los demás— y, con una sola opción (una
      // Part Reference sin workflows), la elegiría solo: las dos cosas las decide aquí la columna.
      sortValues: false,
      autoSelectFirst: false,
      // H179 (RN6/C4): una OF sin workflow lo dice en su propia celda. El indicador se deriva de la
      // fila (`workflow: null`), no de un campo nuevo: C4 no añade ninguno. La fila NUEVA sigue siendo el
      // selector del alta; una fila existente en edición monta el campo de la ficha (EM-5).
      renderOnlyInReadMode: (row) => row.id === undefined,
      render: (row) => {
        // Only an EXISTING row (with id) in edit: a new row has no id and keeps the creation selector.
        if (!detailWindowEnabled && canManageWorkflow && row.id !== undefined && editingItem && String(editingItem.id) === String(row.id)) {
          return (
            <Box sx={{ minWidth: '14rem' }}>
              <FabricationOrderWorkflowField
                fabricationOrderId={row.id}
                onAssigned={handleLegacyWorkflowAssigned}
                showAlert={showAlert}
              />
            </Box>
          )
        }
        // H187 (RN1/RN15-RN17): copy code and its origin, from the single formatter; never `workflow.name` (D-J1).
        return rowHasWorkflow(row)
          ? <WorkflowCodeAndOrigin workflow={row.workflow} />
          : <Chip size='small' variant='outlined' color='warning' label={t_('Without workflow')} />
      },
      edit: {
        source: async () => {
          if (editingItem?.partReference?.id) {
            return workflowOptionsWithSentinel(await apiGet('workflows/approved/' + editingItem.partReference?.id), t_)
          }
          if (lineOrder?.partReference?.id) {
            return workflowOptionsWithSentinel(await apiGet('workflows/approved/' + lineOrder.partReference?.id), t_)
          }
        },
        id: 'id',
        field: 'name'
      }
    },
    {
      name: t_('Quantity'),
      field: 'quantity',
      mandatory: true,
      numeric: true,
      defaultNewValue: 1,
      // #36: quantity is only editable on creation; changes go through the Modify quantity
      // dialog (or MOS), which keep fabrication_order_progress totals in sync.
      readOnly: !isAddingNewRow,
      disableGenericSearch: true,
      disableFilter: true
    },
    {
      name: t_('Total Quantity'),
      field: 'totalQuantity',
      readOnly: true,
      hide: isAddingNewRow,
      disableGenericSearch: true,
      disableFilter: true,
      search: false
    },
    {
      name: t_('Quantity of parts manufactured'),
      field: 'quantityOfPartsManufactured',
      readOnly: true,
      hide: isAddingNewRow,
      disableGenericSearch: true,
      disableFilter: true,
      search: false
    },
    {
      name: t_('Status'),
      field: 'status',
      readOnly: true,
      hide: isAddingNewRow,
      // Story 204 — S71: the lifecycle enum (PENDING/PLANNED/LAUNCHED/CLOSED) by its code; the filter offers the codes.
      render: (row) => systemValueLabel(t_, 'FabricationOrderGlobalStatusEnum', row.status),
      filterByLabel: { options: enumFilterOptions(t_, 'FabricationOrderGlobalStatusEnum') }
    },
    {
      // Story 178 (S73, DP7): the calculated status is shown TRANSLATED with the same keys as the detail's
      // General block (`PARTIALLY_FINISHED` -> "Partially finished"); it is computed server-side, so the column
      // deliberately keeps no filter and no generic search.
      name: t_('Status progress'),
      field: 'statusProgress',
      readOnly: true,
      hide: isAddingNewRow,
      disableGenericSearch: true,
      disableFilter: true,
      search: false,
      render: (row) => statusProgressLabel(t_, row.statusProgress)
    },
    {
      // T7 (S272/RN71): editing an existing row moves to the detail's General block, with the
      // same validations and permissions (RN2) — only creation (S273) still edits this field here.
      name: t_('Target date'),
      field: 'targetDate',
      mandatory: true,
      type: 'dateTimePicker',
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow
    },
    {
      name: t_('Delivery date'),
      field: 'deliveryDate',
      type: 'dateTimePicker',
      defaultNewValue: '',
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow
    },
    {
      name: t_('Min start date'),
      field: 'minStartDate',
      type: 'dateTimePicker',
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow
    },
    {
      name: t_('Planned end date'),
      field: 'plannedEndDate',
      type: 'dateTimePicker',
      readOnly: true,
      // Presentational only (#30): keep the exact date-time format and empty-when-null
      // behavior the table applies to dateTimePicker columns, adding a warning icon +
      // tooltip when the order will finish later than its target date (isLate).
      render: (row) => {
        const formatted = formatDateTime(row.plannedEndDate)
        if (!isPlannedEndDateLate(row.plannedEndDate, row.targetDate)) {
          return formatted
        }
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            {formatted}
            <Tooltip title={t_('Planned end date exceeds target date')}>
              <WarningAmber color='warning' fontSize='small' aria-label={t_('Planned end date exceeds target date')} />
            </Tooltip>
          </span>
        )
      }
    },
    {
      name: t_('Parent FO'),
      field: 'parentFo.fabricationOrder',
      type: 'smartSelect',
      // ⚠️ E20 (completed 30/09): EDITABLE in the legacy window before T7; T7 made it read-only
      // with the rest and E20 missed it. With the switch off it is editable again.
      readOnly: detailWindowEnabled && !isAddingNewRow,
      edit: {
        sourceWithSearch: async (row, search) => {
          const pageable = {
            columnFilters: ['fabricationOrder'],
            columnFilterValues: [search],
            rows: [],
            page: 0,
            size: 10,
            allSelected: false
          }
          return (await apiGet('allFabricationOrderSimpleData' + (row.id ? '/' + row.id : ''), pageable)).content
        },
        id: 'id',
        field: 'fabricationOrder'
      }
    },
    {
      name: t_('Priority'),
      field: 'priority',
      numeric: true,
      mandatory: true,
      defaultNewValue: 5,
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow,
      condition: (row) => {
        return PriorityConditionChecker(row.priority, t_)
      }
    },
    {
      name: t_('Client code'),
      field: 'client.code',
      type: 'smartSelect',
      defaultNewValue: client,
      autoSelectFirst: false,
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow,
      edit: {
        source: async () => {
          return await apiGet('clientsBasicData')
        },
        id: 'id',
        field: 'code'
      }
    },
    {
      name: t_('Client name'),
      field: 'client.name',
      type: 'smartSelect',
      autoSelectFirst: false,
      defaultNewValue: client,
      disabled: true
    },
    {
      name: t_('Order'),
      field: 'order.code',
      render: (row) => {
        return row.order && row.order.code ? row.order.code : ''
      }
    },
    {
      name: t_('Line'),
      field: 'lineOrder.lineNumber',
      render: (row) => {
        return row.lineOrder && row.lineOrder.lineNumber ? row.lineOrder.lineNumber : ''
      }
    },
    {
      name: t_('Order date'),
      field: 'lineOrder.targetDate',
      render: (row) => {
        if (!row.lineOrder || !row.lineOrder.targetDate) {
          return ''
        }
        return formatDateTime(row.lineOrder.targetDate)
      }
    },
    {
      name: t_('Forecast'),
      field: 'forecast',
      type: 'boolean',
      defaultNewValue: order?.forecast || false,
      // ⚠️ E20 (completed 30/09): before T7 only an order-scoped list locked it (`readOnly: order`).
      readOnly: order || (detailWindowEnabled && !isAddingNewRow)
    },
    {
      name: t_('Forecast Expir. Date'),
      field: 'expirationDate',
      type: 'dateTimePicker',
      // ⚠️ E20: esta columna era EDITABLE en la ventana antigua y T7 la puso de solo lectura al
      // retirar la edición en línea (S272). Con el interruptor apagado tiene que volver a serlo, o
      // la fila entra en modo edición y no hay un solo campo que tocar — que es justo lo que vio el
      // PO. `!isAddingNewRow` se conserva para la ventana nueva, donde crear sigue existiendo.
      readOnly: detailWindowEnabled && !isAddingNewRow
    },
    {
      name: t_('Plan'),
      field: 'fabricationOrderPlanTypeDto.code',
      type: 'select',
      defaultNewValue: { code: 'ASAP', id: 1 },
      // ⚠️ E20 (completed 30/09): EDITABLE in the legacy window before T7; T7 made it read-only
      // with the rest and E20 missed it. With the switch off it is editable again.
      readOnly: detailWindowEnabled && !isAddingNewRow,
      edit: {
        source: async () => {
          return await apiGet('getFabricationOrderPlanTypes')
        },
        id: 'id',
        field: 'code'
      },
      mandatory: true
    },
    // S40 (CA2/RN8/INV5, historia 107): provenance of the order, read from the additive DTO field
    // `createdNode`, which is an OBJECT (`ClusterDto`: `{ id, name }`) and is null for an order
    // created here -- so the name has to be reached into, and an own order shows NOTHING, never an
    // invented "Local". The column only exists while the cluster fabrication order sync is enabled:
    // with the switch off the window shows no trace of the feature (S54), and out of a cluster it
    // is exactly as it is today (S39).
    ...(clusterFabricationOrderSyncEnabled
      ? [{
          name: t_('Creation node'),
          field: 'createdNode',
          readOnly: true,
          search: false,
          render: (row) => row?.createdNode?.name || ''
        }]
      : []),

    // S65/S72 (historia 108): how this order stands with the node holding its replica, read from the
    // additive DTO fields `alignment` and `alignmentReason` of the listing this window already
    // fetches -- no second call per page. BOTH are absent for an order that was never synchronised,
    // and that shows NOTHING: no default is invented (INV19/S68), exactly as the provenance column
    // above shows nothing for an order created here.
    ...(clusterAlignmentEnabled
      ? [{
          name: t_('Alignment with the node'),
          field: 'alignment',
          readOnly: true,
          search: false,
          render: (row) => alignmentCellText(row, t_)
        }]
      : [])

  // `detailWindowEnabled` entra en las dependencias porque seis columnas cambian de solo lectura a
  // editables con él (E20): sin declararlo, el ajuste llegaría tarde y la tabla seguiría con las
  // columnas de la ventana equivocada hasta el siguiente recálculo por otro motivo.
  ], [allFoMode, canManageWorkflow, client, handleLegacyWorkflowAssigned, showAlert, clusterAlignmentEnabled, clusterFabricationOrderSyncEnabled, detailWindowEnabled, editingItem, formatDateTime, isAddingNewRow, lineOrder?.partReference?.id, mainImageInfoMap, mainImageThumbnailMap, order, t_, effectivePartReferenceId, mergeMainImages])

  const getColumns = () => {
    if (extendedColumns?.length === 0) {
      return columns
    }
    return columns.concat(extendedColumns)
  }

  // #2819 — this MUST keep a stable identity between renders. SmartMateriaUITable lists `dataFetch`
  // in the dependencies of the effect that recomputes the "N items selected" count and calls it from
  // there (SmartMateriaUITable.jsx:941-961). Since fetching resolves the main images, and that
  // stores new map objects, every fetch re-renders this component: with an inline function the table
  // would see a new `dataFetch` on each of those renders, re-run the effect, fetch again and loop
  // without any throttling for as long as a row stays selected.
  const dataFetch = useCallback(async (pagination) => {
    let data
    if (allFoMode) {
      // H179 (C4): un parámetro PROPIO, fuera de `RequestPagination` (no hay operador "vacío", V14). EM-6
      // (RN6-bis): con el botón suelto `withoutWorkflow=false` las deja fuera; pulsado no se envía (todas).
      const query = hideWithoutWorkflow ? { ...pagination, withoutWorkflow: false } : pagination
      if (allValue) {
        data = await apiGet('allFabricationOrder', query)
      } else {
        data = await apiGet('notClosedfabricationOrder', query)
      }
    } else {
      data = await apiGet('lineOrder/' + lineOrder.id + '/fabricationOrder', pagination)
    }
    const rows = Array.isArray(data) ? data : (data?.content || [])
    resolveMainImages(rows.map((row) => effectivePartReferenceId(row)))
    rows.forEach((row) => {
      if (row?.id !== undefined && row.status) {
        statusByFabricationOrderId.current[String(row.id)] = row.status
      }
      if (row?.id !== undefined) {
        // Sun always sends the key: `"workflow": null` is an order without workflow.
        withoutWorkflowByFabricationOrderId.current[String(row.id)] = Object.prototype.hasOwnProperty.call(row, 'workflow') && !rowHasWorkflow(row)
      }
    })
    return data
  }, [allFoMode, allValue, hideWithoutWorkflow, lineOrder?.id, resolveMainImages, effectivePartReferenceId])

  const checkClientValue = (row) => {
    if (!row.client) {
      row.client = null
      return
    }
    if (Object.keys(row.client).length === 0) {
      row.client = null
    }
  }

  // S32 (RN21): the request is fired without a confirmedDeliveryDate the first time; the
  // frontend never sends skipDeliveryValidation (RN17 — always validated).
  const callFabricationOrderApi = (mode, row) => {
    return mode === 'create' ? apiPost('fabricationOrder', row) : apiPut('fabricationOrder', row)
  }

  // S33 (CA17/RN7,RN14): a "requires confirmation" answer is a 409 carrying a
  // RequiresConfirmationDto ({ code, targetDate, suggestedDate, message }), distinguishable
  // from a plain business/technical error by status + code (never by message shape).
  const isRequiresDeliveryConfirmationError = (error) =>
    error?.status === 409 && ['REQUIRES_CONFIRMATION', 'SUGGESTION_CHANGED'].includes(error?.data?.code)

  // S33/S34 (RN15): opens (or re-opens, on SUGGESTION_CHANGED) the confirmation dialog,
  // remembering the dates to display plus the mode/row/suggestedDate needed to retry on accept.
  // A non-null `message` means a custom processor supplied its own text (RN14/CA7).
  const openDeliveryConfirmationDialog = (mode, row, confirmationData) => {
    setDeliveryConfirmationDialog({
      open: true,
      customMessage: confirmationData.message || '',
      targetDate: confirmationData.targetDate || '',
      suggestedDate: confirmationData.suggestedDate || '',
      mode,
      row
    })
  }

  // S33 (RN14/CA7): the dialog body. A custom processor text is shown verbatim (never
  // re-translated/altered); otherwise the requested date and the suggested slot are laid out as
  // a two-row comparison table so both dates read clearly instead of on a single line, with the
  // suggested slot highlighted as the value the user is being asked to confirm.
  const renderDeliveryConfirmationContent = () => {
    const { customMessage, targetDate, suggestedDate } = deliveryConfirmationDialog
    if (customMessage) {
      return <Typography sx={{ margin: '1em' }}>{customMessage}</Typography>
    }
    return (
      <Stack spacing={2} alignItems='center' sx={{ padding: '0.5em 1em', margin: '0 auto', maxWidth: '34rem' }}>
        <Typography>{t_('The fabrication order will not be ready by the requested delivery date.')}</Typography>
        <Box
          component='table'
          sx={{
            borderCollapse: 'collapse',
            '& td': {
              padding: '0.5rem 1rem',
              border: theme => '1px solid ' + theme.palette.divider
            }
          }}
        >
          <tbody>
            <tr>
              <Box component='td' sx={{ textAlign: 'left', color: 'text.secondary' }}>{t_('Requested delivery date')}</Box>
              <Box component='td' sx={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatDateTime(targetDate)}</Box>
            </tr>
            <tr>
              <Box component='td' sx={{ textAlign: 'left', color: 'text.secondary' }}>{t_('Next available slot')}</Box>
              <Box component='td' sx={{ textAlign: 'right', fontWeight: 700, color: 'warning.main', whiteSpace: 'nowrap' }}>{formatDateTime(suggestedDate)}</Box>
            </tr>
          </tbody>
        </Box>
        <Typography>{t_('Do you want to confirm this new delivery date?')}</Typography>
      </Stack>
    )
  }

  // S32 (CU6/RN21): wraps the create/edit call with the loading indicator + duplicate-submit
  // block. `mode` is 'create' or 'edit'.
  const saveFabricationOrderWithDeliveryValidation = (mode, row) => {
    setIsSavingFabricationOrder(true)
    return callFabricationOrderApi(mode, row)
      .then((data) => {
        setIsSavingFabricationOrder(false)
        return data
      })
      .catch((error) => {
        setIsSavingFabricationOrder(false)
        if (isRequiresDeliveryConfirmationError(error)) {
          // S33: open the confirmation dialog instead of propagating a generic error. The
          // promise returned to the table is deliberately left pending forever — the outcome
          // (persist on accept, S34; or nothing, S35) is fully owned by the dialog, not by the
          // table's own generic success/error handling.
          openDeliveryConfirmationDialog(mode, row, error.data)
          return new Promise(() => {})
        }
        // sun #3936: the table shows `error.message`; this case gets a plain sentence that says what to fix. Every
        // other error reaches the table untouched, as before.
        if (isNoMachineForTechnologyError(error)) {
          error.message = fabricationOrderSaveErrorMessage(error, t_)
        }
        throw error
      })
  }

  // T7 (S270/S271/S279/RN71): the row keeps only the identity/navigation gesture ("Open detail")
  // and Borrar — the inline "Edit" toggle and every other row-level action (Modify quantity,
  // Workflow, Balanced, Documents, Copy as, Pause/Resume FO, View Associated Parts, FO progress,
  // Generate QR) move into the detail (`FabricationOrderDetailDialog`), reachable exclusively
  // through "Open detail" now. No `actions.edit` — the whole row is read-only (S272), and there is
  // no `allFoMode` gate on "Open detail": the line-order modal offers exactly the same row (S274).
  // ⚠️ VENTANA ANTIGUA (E20) — efectos y manejadores de las nueve acciones. VERBATIM de d006bd395.
  useEffect(() => {
    const loadAllowEdit = async () => {
      const allowedIndexValues = await apiGet('indexValues/available')
      setAllowEdit(!!allowedIndexValues.find((item) => item.name === 'APPROVED'))
    }
    loadAllowEdit()
  }, [])

  // ⚠️ MERGE de develop (H145, #2946) — va AQUÍ, dentro del bloque de la ventana ANTIGUA, y no en
  // la rama encendida. La acción de fila "Flujo de trabajo" es de las NUEVE de la ventana antigua:
  // con el interruptor encendido el menú de fila solo ofrece "Abrir detalle" y "Eliminar", así que
  // este efecto no se dispara nunca por ahí. Y el addendum 14 exige que apagado se comporte como se
  // comportaría hoy si esta historia no lo hubiera tocado, INCLUIDO lo que develop le añada después:
  // dejarlo fuera habría degradado la ventana antigua respecto de develop, que es justo lo que la
  // convivencia existe para impedir.
  //
  // (El detalle nuevo abre el workflow por su propio camino —`handleOpenWorkflow` en el diálogo—,
  // que no pasa por `selectedWorkflowFabricationOrderId` y por tanto no comparte este indicador.)
  //
  // H145 (RN1-RN6/CA1-CA3): el indicador sube ANTES de esperar la petición (RN1), toda la petición
  // va envuelta para atrapar un error (RN5) y el desenlace SIEMPRE retira el indicador y limpia el
  // id pendiente (RN4/DI3) — pero nunca el estado de la OF (R1).
  // RN6: en 401/502/503/504 `apiF` llama a notifyServerConnectionLost() y resuelve `undefined` SIN
  // lanzar, así que un try/catch a secas no vería ese camino y abriría el editor con `undefined`.
  useEffect(() => {
    const getWorkflow = async () => {
      setIsLoadingWorkflow(true)
      try {
        // Clear previous workflow
        setCurrentWorkflow(null)
        setCurrentOriginalWorkflow(null)
        setNodes([])
        const data = await apiGet('workflowByFabricationOrder/' + selectedWorkflowFabricationOrderId)
        if (data?.id !== undefined && data?.id !== null) {
          const workflow = clearTempDocumentScopes(data)
          setCurrentWorkflow(workflow)
          setCurrentOriginalWorkflow(structuredClone(workflow))
          setShowWorkflowDialog(true)
        }
      } catch (error) {
        showAlert({
          title: t_('Error loading data'),
          message: error.message ? t_(error.message) : t_('Error loading data'),
          severity: error.name
        })
      } finally {
        setIsLoadingWorkflow(false)
        setSelectedWorkflowFabricationOrderId(null)
      }
    }
    if (selectedWorkflowFabricationOrderId) {
      getWorkflow()
    }
  }, [selectedWorkflowFabricationOrderId, setNodes])

  useEffect(() => {
    const getBalancedWorkflow = async () => {
      const base = selectedBalancedRow.workflow
      // Ensure partReference (exposed at row level) is present for SET_WORKFLOW.
      // The modal opens even when the workflow is not balanced yet, so the user can define it.
      const workflow = { ...base, partReference: base.partReference ?? selectedBalancedRow.partReference }
      setBalancedWorkflow(workflow)
      setShowBalancedDialog(true)
      setSelectedBalancedRow({})
    }
    if (selectedBalancedRow.workflow?.id) {
      getBalancedWorkflow()
    }
  }, [selectedBalancedRow])

  const dialogEditCopy = (
    <Grid container spacing={1} marginTop={2}>
      {AlertElementDialog}
      <Grid item xs={3}>
        <TextField
          fullWidth
          margin='normal'
          value={ofCopy?.fabricationOrder}
          readOnly
          disabled
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          margin='normal'
          label='Quantity'
          type='number'
          value={formData.quantity}
          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
        />
      </Grid>
      <Grid item xs={3} marginTop='16px'>
        <DateTimePicker
          fullWidth
          margin='normal'
          label='Target date'
          value={formData.targetDate}
          format={format.dateTime}
          onChange={(newValue) => setFormData({ ...formData, targetDate: newValue })}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              error={formData.targetDateError}
            />
          )}
          helperText={formData.targetDateError ? t_('Delivery date must be later than or equal to the order date') : ''}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          margin='normal'
          label='Priority'
          type='number'
          value={formData.priority}
          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          inputProps={{ min: 1, max: 10 }}
          error={formData.priorityError}
          helperText={formData.priorityError ? t_('Priority must be greater than 0 and less than or equal to 10') : ''}
        />
      </Grid>
    </Grid>
  )

  const generateCopyNumber = async (originalOrder) => {
    try {
      return await apiGet('getFabricationOrderNumberCopy/' + originalOrder)
    } catch (error) {
      showAlert({
        title: t_('Error getting copy data'),
        message: t_(error.message),
        severity: error.name
      })
    }
  }

  const handleAcceptButtonClick = async () => {
    if (!formData.targetDate || formData.targetDate === ' ') {
      showAlertDialog({
        title: t_('Error'),
        message: t_('Delivery date cannot be null'),
        severity: 'error'
      })
      setFormData(prevData => ({
        ...prevData,
        targetDateError: true
      }))
      return
    }
    const orderDate = moment(ofCopy.date)
    const targetDate = moment(formData.targetDate)
    if (!targetDate.isValid() || targetDate.isBefore(orderDate)) {
      showAlertDialog({
        title: t_('Error'),
        message: t_('Delivery date must be later than the order date'),
        severity: 'error'
      })
      setFormData(prevData => ({
        ...prevData,
        targetDateError: true
      }))
      return
    }
    const priority = PriorityConditionChecker(formData.priority)
    if (priority !== '') {
      showAlertDialog({
        title: t_('Error'),
        message: priority,
        severity: 'error'
      })
      setFormData(prevData => ({
        ...prevData,
        priorityError: true
      }))
      return
    }
    ofCopy.targetDate = formData.targetDate
    ofCopy.quantity = formData.quantity
    ofCopy.priority = formData.priority
    ofCopy.id = null
    setOpenCopyDialog(false)
    try {
      // H179 (RN15): copiar una OF sin workflow crea otra sin workflow; con workflow, como hoy.
      await apiPost('fabricationOrder', copyBodyOf(ofCopy))
      setFormData(prevData => ({
        ...prevData,
        targetDateError: false
      }))
      showAlert({
        title: t_(''),
        message: t_('Copied correctly'),
        severity: 'success'
      })
    } catch (error) {
      showAlert({
        title: t_('Error creating fabrication order'),
        message: fabricationOrderSaveErrorMessage(error, t_),
        severity: error.name
      })
    }
  }

  const handleConfirmationAccept = async () => {
    setConfirmationDialogOpen(false)
    if (confirmationAction === 'pause') {
      const data = await apiPut('order/pause/' + rowIdToConfirm)
      showAlert({
        title: t_('Part(s) has been paused'),
        severity: 'success'
      })
      if (data.length > 0) {
        const finishedParts = []
        const partsOnDevice = []
        // Separate different data
        data.forEach(element => {
          if (element.status === 'FINISHED') {
            finishedParts.push(element)
          } else {
            partsOnDevice.push(element)
          }
        })
        setUnfinishedPartsPauseResume(finishedParts)
        setPartsOnDevicesToPause(partsOnDevice)
      }
    } else if (confirmationAction === 'resume') {
      const data = await apiPut('order/resume/' + rowIdToConfirm)
      showAlert({
        title: t_('Part(s) has been resumed'),
        severity: 'success'
      })
      if (data.length > 0) {
        setUnfinishedPartsPauseResume(data)
      }
    }
  }

  const handleCopyButtonClick = async (row) => {
    try {
      const copyNumber = await generateCopyNumber(row.fabricationOrder)
      const rowCopy = { ...row }
      rowCopy.fabricationOrder = row.fabricationOrder + '-' + copyNumber
      setOfCopy(rowCopy)
      setFormData({
        quantity: row.quantity,
        targetDate: '',
        priority: row.priority
      })
      setOpenCopyDialog(true)
    } catch (error) {
      showAlert({
        title: t_('Error generating copy number'),
        message: t_(error.message),
        severity: error.name
      })
    }
  }

  // S22-S26: Modify quantity — open the modal for a given row (the whole flow lives in
  // ModifyQuantityDialog; here we only record which row it targets).
  const handleModifyQuantityOpen = (row) => {
    setModifyQuantityDialog({ open: true, row })
  }

  const handlePauseResumeClick = async (action, rowId) => {
    setConfirmationDialogOpen(true)
    setConfirmationAction(action)
    setRowIdToConfirm(rowId)
  }

  const onPauseWorkflowForce = async (partId, endJob) => {
    partsOnDevicesToPause.shift()
    setPartsOnDevicesToPause([...partsOnDevicesToPause])
    await apiPut('part/pause/' + partId + '/' + endJob)
  }

  const partDialogDataFetch = async (pagination) => {
    const parts = await apiGet('partsFabricationOrder/' + currentFabricationOrder.id, pagination)
    return parts
  }

  const saveWorkflow = async () => {
    if (savingWorkflowRef.current) {
      return
    }
    savingWorkflowRef.current = true
    setIsSavingWorkflow(true)
    try {
      await persistWorkflow()
    } finally {
      savingWorkflowRef.current = false
      setIsSavingWorkflow(false)
    }
  }

  const persistWorkflow = async () => {
    // Story 80 (S54): this dialog saves the fabrication order's workflow through its OWN function, so it
    // never goes through Workflow.jsx and never runs validateWorkflow. Without this call the client check
    // would not exist in a fabrication order at all — which is exactly where a job can be externalised.
    if (findNewExternalJobMissingReturnTiming(currentWorkflow?.nodes)) {
      showAlert({
        title: t_('An external job must define its return timing'),
        severity: 'warning',
        message: ''
      })
      return
    }

    const diff = diffObjects(currentOriginalWorkflow, currentWorkflow, ['workflowPositions'])
    // `document/checkTemp` matches TEMP UUIDs (the keys of Sun's temp document map), not reference
    // UUIDs — sending the latter never matched, so an edit whose only change was a document was
    // reported as "No changes to save" and never saved.
    const existsPendingDocuments = await apiPut('document/checkTemp', collectTempDocumentUUIDs(currentWorkflow))
    if (diff.equals && !existsPendingDocuments.value) {
      // No changes
      setShowWorkflowDialog(false)
      showAlert({ title: t_('No changes to save'), severity: 'info', message: '' })
      return
    }

    currentWorkflow.workflowPositions = updatePositions(nodes, currentWorkflow)
    // Story 183 (C1, RN8.1): the night mark goes explicit on every job, or Sun would keep the persisted one.
    // Story 186 (S114): same feature group payload preparation as the editor of the templates.
    const workflowToSave = await prepareFabricationOrderWorkflowForSave(currentWorkflow, currentOriginalWorkflow)
    await apiPut('workflow/false/false/' + currentWorkflow.indexValue.name + '/' + currentWorkflow.partReferenceId, withExplicitNightJob(workflowToSave)).then(() => {
      showAlert({ severity: 'success', title: t_('Workflow updated correctly'), message: '' })
      setShowWorkflowDialog(false)
      reloadTable()
    }).catch((error) => {
      // S64/D20/E6: same duplicate-planning-group rejection as the templates route
      // (`Workflow.jsx`'s own save), but this route's `.catch` only ran `showAlert` before this
      // change — so the fabrication order's workflow was ALREADY left untouched and the dialog
      // ALREADY stayed open on any error. What S64 adds is the correct, translated (S65) message
      // naming the offending value(s), instead of the generic one.
      if (isDuplicatePlanningGroupSaveError(error)) {
        showAlert({
          severity: 'error',
          message: t_(DUPLICATE_PLANNING_GROUP_MESSAGE_KEY, { values: extractDuplicatePlanningGroupValues(error).join(', ') })
        })
        return
      }
      // Story 80 (S44): show the server's own message instead of dropping it. A 409 from this story names
      // the offending job, and a generic "Error saving workflow" hides exactly the part the user needs.
      // The template editor already does this (Workflow.jsx paints t_(error.message)); this dialog did not.
      showAlert(alertForSaveError(error, t_))
    })
  }
  // ⚠️ FIN de los manejadores de la ventana antigua.

  // ⚠️ VENTANA ANTIGUA (E20) — las nueve acciones de fila que T7 retiró, VERBATIM de d006bd395.
  const legacyRowActions = [
    {
      // S22-S26: Modify quantity (D6/RN-QTY-1) — gated by FABRICATION ORDERS permission (D2/A1)
      name: 'Modify quantity',
      render: (row) => {
        return (
          <IconButton>
            <EditNote />
          </IconButton>
        )
      },
      onClick: (row) => handleModifyQuantityOpen(row),
      disabled: (row) => editing || !isUserAuthorized('FABRICATION ORDERS')
    },
    {
      name: 'Workflow',
      render: (row) => {
        return (
          <IconButton>
            <Schema />
          </IconButton>
        )
      },
      onClick: (row) => {
        // ⚠️ MERGE de develop (H145, #2946). Va aquí y no en la rama encendida: esta acción de fila
        // es de las nueve de la ventana ANTIGUA. RN3/CA2, una carga cada vez — se ignora mientras
        // otra sigue en vuelo, venga de esta fila o de cualquier otra.
        if (isLoadingWorkflow || !rowHasWorkflow(row)) {
          return
        }
        setLegacyRowSensitive(row.sensitive === true)
        setSelectedWorkflowFabricationOrderId(row.id)
        setSelectedWorkflowFabricationOrderStatus(row.status)
      },
      // H179 (RN5/T20): una OF sin workflow no tiene editor que abrir. Antes de esta historia la fila
      // no podía llegar (el listado las ocultaba) y `row.workflow.used` reventaba con `workflow: null`.
      disabled: (row) => editing || !isUserAuthorized('WORKFLOWS_TAB') || !rowHasWorkflow(row) || row.workflow.used
    },
    {
      name: 'Balanced',
      render: (row) => {
        return (
          <IconButton>
            <Balance />
          </IconButton>
        )
      },
      onClick: (row) => {
        if (!rowHasWorkflow(row)) return
        setLegacyRowSensitive(row.sensitive === true)
        setSelectedBalancedRow(row)
      },
      // H179 (RN5): tampoco hay nada que balancear.
      disabled: (row) => editing || !isUserAuthorized('WORKFLOWS_TAB') || !rowHasWorkflow(row)
    },
    {
      name: 'Documents',
      render: (row) => {
        return (
          <IconButton>
            <TextSnippetOutlined />
          </IconButton>
        )
      },
      onClick: (row) => {
        const data = { id: row.id, code: row.fabricationOrder, name: row.fabricationOrder }
        setLegacyRowSensitive(row.sensitive === true)
        setFabricationOrderRow(data)
        setShowDocument(true)
      },
      disabled: editing || !isUserAuthorized('DOCUMENTS_FABRICATION_ORDER')
    },
    {
      name: 'Copy as',
      render: (row) => {
        return (
          <IconButton>
            <ContentCopyIcon />
          </IconButton>
        )
      },
      onClick: (row) => handleCopyButtonClick(row),
      disabled: editing || !isUserAuthorized('COPY_FABRICATION_ORDERS')
    },
    {
      name: 'Resume FO',
      render: (row) => {
        return (
          <IconButton>
            <PlayCircleFilledOutlined />
          </IconButton>
        )
      },
      onClick: async (row) => { await handlePauseResumeClick('resume', row.id) },
      disabled: editing || !isUserAuthorized('RESUME_FO_FABRICATION_ORDERS')
    },
    {
      name: 'Pause FO',
      render: (row) => {
        return (
          <IconButton>
            <PauseCircle />
          </IconButton>
        )
      },
      onClick: async (row) => { await handlePauseResumeClick('pause', row.id) },
      disabled: editing || !isUserAuthorized('PAUSE_FO_FABRICATION_ORDERS')
    },
    {
      name: 'View Associated Parts',
      render: (row) => {
        return (
          <IconButton>
            <ManageSearchOutlined />
          </IconButton>
        )
      },
      onClick: (row) => {
        setCurrentFabricationOrder(row)
        setOpenPartsDialog(true)
      },
      disabled: editing || !isUserAuthorized('VIEW_PARTS_FABRICATION_ORDERS')
    },
    {
      name: 'FO progress',
      render: (row) => {
        return (
          <IconButton>
            <HourglassTopOutlined />
          </IconButton>
        )
      },
      onClick: (row) => {
        setCurrentFabricationOrder(row)
        setOpenProgressDialog(true)
      },
      disabled: editing || !isUserAuthorized('VIEW_PARTS_FABRICATION_ORDERS')
    },
    // H2-S9 / RN-H2-5: "Generate QR" only in the management grid (allFoMode), never in the
    // embedded accordion of Order/LineOrder (allFoMode absent/falsy).
    ...(allFoMode
      ? [{
          name: 'Generate QR',
          render: (row) => (
            <IconButton>
              <QrCode2Icon />
            </IconButton>
          ),
          disabled: editing || !isUserAuthorized('GENERATE_QR_CODE'),
          onClick: (row) => {
            setQrRow(row)
            setShowQr(true)
          }
        }]
      : [])
  ]
  // ⚠️ FIN de las acciones de la ventana antigua.

  const actions = {
    // ⚠️ E20: la edición en línea SOLO existe con la ventana antigua. T7 la retiró (S272) porque
    // editar pasó a hacerse en el bloque General del detalle; apagado el interruptor vuelve tal cual.
    ...(detailWindowEnabled
      ? {}
      : {
          edit: {
            fetchFunction: (row) => {
              checkClientValue(row)
              if (!allFoMode) {
                row.order = order
                row.lineOrder = lineOrder
                row.partReference = lineOrder.partReference
              }
              return saveFabricationOrderWithDeliveryValidation('edit', row)
            },
            // Revision of historia 40 (#2819): S25's row-level gate is gone — a fabrication order whose
            // current status the profile does not own (e.g. CLOSED) is editable again, and so is one whose
            // parent order sits in a non-owned status. The FO `status` column is read-only for everybody
            // anyway: the only way to change it is the bulk selector, which still enforces the
            // FO_STATUS_PERM_* transition gating (S36).
            disabled: !isUserAuthorized('EDIT_FABRICATION_ORDERS')
          }
        }),
    create: {
      fetchFunction: (row) => {
        checkClientValue(row)
        if (!allFoMode) {
          row.order = order
          row.lineOrder = lineOrder
          row.partReference = lineOrder.partReference
        }
        row.quantityOfPartsManufactured = null
        row.status = null
        // H179 (C3/RN9): la opción "Without workflow" se traduce aquí, por su identidad. Y el cuerpo
        // traducido es el que se guarda para el reintento de la confirmación de entrega.
        return saveFabricationOrderWithDeliveryValidation('create', creationBodyOf(row))
      },
      disabled: !isUserAuthorized('ADD_RECORD_FABRICATION_ORDERS')
    },
    delete: {
      fetchFunction: async (pagination) => {
        if (Array.isArray(pagination)) {
          // When one selected instead of the pagination we get an array with a unique element that is the row
          const newPagination = {
            page: 0,
            size: -1,
            columns: [],
            genericSearch: '',
            rows: [],
            allSelected: false,
            primaryKeys: [
              'id'
            ],
            sortKey: [],
            sortMode: []
          }
          pagination.map((item) => newPagination.rows.push(item.id))
          pagination = newPagination
        }
        setShowDialog({ open: true, rowValues: pagination })

        return { disableAlert: true }
      },
      disabled: !isUserAuthorized('DELETE_FABRICATION_ORDERS')
    },
    custom: [
      // ⚠️ ADDENDUM 14 (E20) — AQUÍ ELIGEN LAS DOS VENTANAS. Encendido, la fila ofrece "Abrir
      // detalle"; apagado, las nueve acciones que T7 retiró. No conviven en la misma fila: son dos
      // formas de trabajar, no dos conjuntos que se sumen.
      // ⚠️ Y "como si esta historia no lo hubiera tocado" incluye lo que develop añadió DESPUÉS: la
      // acción de resincronizar (historia 108) se ofrece en las DOS, porque no es una de las que se
      // retiraron — es trabajo posterior y ajeno a este reparto.
      ...(detailWindowEnabled
        ? [{
            // T7 (S270/S271/S274/S278): the ONLY way into the row's own management — every action
            // this used to expose row-by-row (RN71) lives in the detail now, including "Generate QR"
            // (S278: gated on Sun's own `qrAvailable`, never on `allFoMode` — D26 deliberately
            // derogates the old RN-H2-5 gate). No permission gates "Open detail" itself: it only reads.
            name: 'Open detail',
            render: () => (
              <OpenInNewIcon />
            ),
            onClick: (row) => setDetailDialog({ open: true, fabricationOrderId: row.id, sensitive: row.sensitive === true })
          }]
        : legacyRowActions),
      // ⚠ MERGE develop (pasada 34): esta acción es de la historia 108 y NO estaba cuando T7
      // repartió las acciones de fila, así que no es una de las que se retiraron: es trabajo de
      // otra historia que tiene que seguir funcionando. Se conserva tal cual venía.
      // Ojo a la interacción entre historias: T7 dejó la fila con "Open detail" y "Delete", y
      // esto añade una tercera SOLO en instalación en cluster con la alineación encendida y el
      // permiso de envío. Fuera de cluster no se monta y la fila sigue como la verificó el hub.
      // S69/S102 (historia 108): resincronizar ESTA OF con el otro nodo. Solo existe con la
      // alineación encendida y con el permiso de envío al cluster; sin una de las dos la ventana no
      // muestra rastro de la funcionalidad (S71). Por fila queda deshabilitada mientras la OF no
      // tenga indicador: sin haberse sincronizado nunca no hay nada que realinear.
      // (S102 quedó RETIRADO por alcance en la vuelta 1 del Judge: en la célula una réplica nunca trae
      // indicador, porque solo el nodo emisor escribe el estado de sincronización.)
      ...(clusterResyncEnabled
        ? [{
            name: 'Resynchronise with node',
            render: (row) => (
              <IconButton>
                <SyncIcon />
              </IconButton>
            ),
            onClick: (row) => setResyncRow(row),
            disabled: (row) => editing || !hasAlignmentIndicator(row)
          }]
        : [])
    ]
  }

  const multipleSelectionActions = [
    {
      render: (pendingChanges, index, pageable, multipleSelectionValue) => {
        const changeStatus = async (newStatus) => {
          try {
            // S119-S122: only CLOSED goes through the forced-return gate — PENDING/LAUNCHED are not
            // "cancel or close" (CU6/RN44) and stay exactly as they were. Sun's structured 409
            // (convergence, lote 17) carries no `fabricationOrderId` of its own -- the gate needs it
            // supplied from here, the first selected row (bulk closes of more than one order are not
            // covered by any scenario of this historia; RN44/CU6/A2 is about several JOBS of ONE
            // order, not several orders at once).
            const request = () => apiPost('fabricationOrderMulti/status/' + newStatus + withoutWorkflowQuery, pageable)
            const fabricationOrderId = (multipleSelectionValue?.rows?.[0]?.id) || ''
            await (newStatus === 'CLOSED' ? forcedReturnGate.runGuarded(request, fabricationOrderId) : request())
            showAlert({ severity: 'success', title: t_('Status changed correctly'), message: '' })
            toogleResetMultipleSelectionValue()
          } catch (error) {
            // Story 137 (E2/CA14/CA16): a return to PENDING refused by active jobs arrives as ONE structured
            // 409 for the whole selection; it is shown as ONE alert with the translated key plus the
            // `fabricationOrderCode — deviceName` list read from error.data.activeJobs. Every other error
            // (E1, E3, E4, forced return) keeps exactly today's path.
            showAlert({
              title: t_('Error changing status:'),
              message: isActiveJobsConflict(error)
                ? buildActiveJobsConflictMessage(t_, error.data)
                : t_(error.message),
              severity: error.name
            })
          }
        }

        // S36 (historia 40): each destination is individually disabled (never removed from the
        // list — PLANNED is the only destination that is never offered at all, E1) unless the
        // profile owns the fixed permission for BOTH the current status of every selected row and
        // the destination status (RN17/CA12). Mirrors the backend's atomic bulk gate
        // (E_BULK_STATE_MIXED): one non-owned row in the selection disables the action for all.
        //
        // The selection only carries primary keys, so each row's current status is recovered here
        // from the id -> status index built while fetching (statusByFabricationOrderId). The gating
        // itself — including the select-all case, where the table empties `rows` — stays in
        // canBulkTransitionFabricationOrderSelection (statusPermissionGating.js).
        const selection = {
          allSelected: !!multipleSelectionValue?.allSelected,
          rows: (multipleSelectionValue?.rows || []).map((row) => ({
            ...row,
            status: row.status || statusByFabricationOrderId.current[String(row.id)]
          }))
        }
        const canTransitionTo = (target) => canBulkTransitionFabricationOrderSelection(isUserAuthorized, selection, target) &&
          !isBulkStatusBlockedByWithoutWorkflow(target, selection, withoutWorkflowByFabricationOrderId.current, statusByFabricationOrderId.current)

        const stateChangeActions = [
          {
            value: 'PENDING',
            label: t_('PENDING'),
            disabled: !canTransitionTo('PENDING'),
            action: () => {
              changeStatus('PENDING')
            }
          },
          {
            value: 'LAUNCHED',
            label: t_('LAUNCHED'),
            disabled: !canTransitionTo('LAUNCHED'),
            action: () => {
              changeStatus('LAUNCHED')
            }
          },
          {
            value: 'CLOSED',
            label: t_('CLOSED'),
            disabled: !canTransitionTo('CLOSED'),
            action: () => {
              changeStatus('CLOSED')
            }
          }
        ]

        return (
          <ActionSelector
            actions={stateChangeActions}
            label={t_('Status')}
            placeholder={t_('Select status')}
            disabled={!isUserAuthorized('FABRICATION_ORDER_STATUS_CHANGE')}
          />
        )
      }

    },
    {
      // S34 (historia 107): a SECOND bulk action, never inside the status ActionSelector, so the
      // actions that were already there keep behaving exactly as before (S38). When the feature is
      // not available the action is ABSENT (null), not disabled -- including a single-node cluster,
      // where no destination is left after removing the current node (S39).
      name: 'Send to node',
      render: (datafetch, index, pageable, multipleSelectionValue) => {
        if (!clusterSendEnabled || clusterTargetNodes.length === 0) {
          return null
        }
        return (
          <FabricationOrderClusterSendAction
            key='fabrication-order-cluster-send'
            nodes={clusterTargetNodes}
            dataFetch={datafetch}
            pageable={pageable}
            multipleSelectionValue={multipleSelectionValue}
            showAlert={showAlert}
            onSent={toogleResetMultipleSelectionValue}
          />
        )
      }
    }
  ]

  // Order by targetDate as default sort
  const sortOptions = {
    default: {
      field: 'targetDate',
      direction: 1
    }
  }
  const validator = (row) => {
    if (row.partReferenceWorkflow?.id === undefined) {
      row.partReferenceWorkflow = null
    }
    // Order and line order
    if (row.order?.id === undefined) {
      row.order = null
    }
    if (row.lineOrder?.id === undefined) {
      row.lineOrder = null
    }
    if (row.order === null && row.lineOrder !== null) {
      return { errorFields: ['order', 'lineOrder'], alertMessage: t_('Line order was set, but order cannot be null') }
    }

    if (row.order !== null && row.lineOrder === null) {
      return { errorFields: ['order', 'lineOrder'], alertMessage: t_('Order was set, but line order cannot be null') }
    }
    // Check quantity > 0
    if (row.quantity === null || row.quantity === undefined || row.quantity <= 0) {
      return { errorFields: ['quantity'], alertMessage: t_('Quantity must be greater than 0') }
    }
    // Check priority
    if (row.priority === null || row.priority === undefined || row.priority <= 0 || row.priority > 10) {
      return { errorFields: ['priority'], alertMessage: t_('Priority must be greater than 0 and less than or equal to 10') }
    }

    // V3: target date must be after order date (target date and delivery date are independent)
    const orderDate = row.date ? moment(row.date, moment.ISO_8601, true) : moment(order?.date, moment.ISO_8601, true)
    const targetDate = moment(row.targetDate, moment.ISO_8601, true)
    if (targetDate.isValid() && orderDate.isValid()) {
      if (targetDate.isBefore(orderDate)) {
        return { errorFields: ['targetDate'], alertMessage: t_('Delivery date must be later than the order date') }
      }
    }

    return false
  }

  const closeDeliveryConfirmationDialog = () => {
    setDeliveryConfirmationDialog({ open: false, customMessage: '', targetDate: '', suggestedDate: '', mode: 'create', row: null })
  }

  // S34 (CU7/RN15): accept → retries the SAME request (mode/row) with confirmedDeliveryDate set
  // to the suggestedDate the user saw, sent EXACTLY as received (never reformatted — the backend
  // compares it at millisecond precision, RN15 caveat). Reusing skipDeliveryValidation is never
  // needed: the frontend always validates (RN17).
  const handleDeliveryConfirmationAccept = async () => {
    const { mode, row, suggestedDate } = deliveryConfirmationDialog
    setIsSavingFabricationOrder(true)
    try {
      const url = 'fabricationOrder?confirmedDeliveryDate=' + encodeURIComponent(suggestedDate)
      const data = mode === 'create' ? await apiPost(url, row) : await apiPut(url, row)
      closeDeliveryConfirmationDialog()
      showAlert({
        title: t_(mode === 'create' ? 'Created correctly.' : 'Updated correctly.'),
        severity: 'success'
      })
      return data
    } catch (error) {
      if (isRequiresDeliveryConfirmationError(error)) {
        // RN15: the recalculated slot moved again since the user saw it (SUGGESTION_CHANGED) —
        // re-open with the fresh suggestion instead of silently persisting a stale date.
        openDeliveryConfirmationDialog(mode, row, error.data)
        return
      }
      closeDeliveryConfirmationDialog()
      showAlert({
        title: '',
        message: t_(error.message),
        severity: error.name
      })
    } finally {
      setIsSavingFabricationOrder(false)
    }
  }

  // S35 (CU7/RN22): cancel — nothing is resent, nothing is persisted, the dialog just closes.
  const handleDeliveryConfirmationCancel = () => {
    closeDeliveryConfirmationDialog()
  }

  const deleteFabricationOrder = async (deleteParts, pagination) => {
    try {
      // S119-S122: if Sun answers "requires confirmation" (E-M13, external jobs still dispatched),
      // the forced-return dialog takes over and this call stays pending until it settles — same
      // pass-through-on-success/re-throw-on-any-other-error contract as before (S122). Sun's
      // structured 409 carries no `fabricationOrderId` of its own -- supplied here from the first
      // selected row (`pagination.rows` holds either row objects or raw ids depending on the caller,
      // see `actions.delete.fetchFunction` above).
      const firstSelectedRow = pagination?.rows?.[0]
      const fabricationOrderId = (firstSelectedRow && typeof firstSelectedRow === 'object' ? firstSelectedRow.id : firstSelectedRow) || ''
      await forcedReturnGate.runGuarded(() => apiDelete('fabricationOrderMulti/' + deleteParts + withoutWorkflowQuery, pagination), fabricationOrderId)
      showAlert({ severity: 'success', title: t_('Deleted correctly'), message: '' })
    } catch (error) {
      showAlert({
        title: t_('Error deleting fabrication orders.'),
        message: t_(error.message),
        severity: error.name
      })
    }
    toogleResetMultipleSelectionValue()
  }

  if (extendedColumns === null || extendedColumns === undefined) {
    return <CircularProgress />
  }

  return (
    <>
      {/* ⚠️ VENTANA ANTIGUA (ADDENDUM 14 / E20) — los diálogos de las nueve acciones, VERBATIM de
          d006bd395. Solo se montan con el interruptor apagado: encendido, todo esto vive dentro del
          detalle y montarlo aquí además sería pagar dos veces por lo mismo.
          Van agrupados en un solo fragmento condicional en vez de repartidos por el árbol como
          estaban: son modales independientes entre sí, así que el orden relativo no cambia nada, y
          agrupados se retiran de una vez el día que se quite el interruptor. */}
      {!detailWindowEnabled && (
        <>
          {/* S22-S26 / S32: Modify quantity modal (form + OK-parts confirmation + creation progress). */}
          <ModifyQuantityDialog
            fabricationOrder={modifyQuantityDialog.row}
            open={modifyQuantityDialog.open}
            onClose={() => setModifyQuantityDialog({ open: false, row: null })}
            showAlert={showAlert}
          />
          <SmartDialog
            cancelCallback={() => setShowWorkflowDialog(false)}
            minWidth='calc(100vw - 64px)'
            renderComponent={
              // El proveedor va FUERA del Suspense a propósito: es el contexto que envuelve, no
              // tiene coste de carga, y así el elemento que el diálogo recibe sigue siendo el mismo
              // que antes de hacer perezoso el editor. Al revés, `renderComponent` pasaría a ser un
              // Suspense y cambiaría la forma del árbol sin necesidad.
              <WorkflowHierarchyProvider>
                <SensitiveBoundary sensitive={legacyWindowSensitive}>
                  <Suspense fallback={<LegacyDialogFallback />}>
                    <Workflow
                      frontendConfig={frontendConfig}
                      initialWorkflow={currentWorkflow}
                      setInitialWorkflow={setCurrentWorkflow}
                      hideButtonsSubWorkflow
                      startInEditMode={allowEdit}
                      nodes={nodes}
                      setNodes={setNodes}
                      onNodesChange={onNodesChange}
                      fromFO
                      allowEdit={allowEdit}
                      fabricationOrderOperable={isFabricationOrderOperable(selectedWorkflowFabricationOrderStatus)}
                    // RN117.2 (story #150): this window accepts on behalf of the editor, so an open row
                    // inside it blocks accepting here too — otherwise the half-typed row is dropped.
                      onSaveAllowedChange={setWorkflowSaveAllowed}
                    />
                  </Suspense>
                </SensitiveBoundary>
              </WorkflowHierarchyProvider>
            }
            acceptCallback={() => { saveWorkflow() }}
            accept={allowEdit}
            disableAccept={!workflowSaveAllowed || isSavingWorkflow}
            acceptDisabledReason={isSavingWorkflow ? t_('Saving...') : t_('Finish editing the open row before saving')}
            setOpen={showWorkflowDialog}
            paperWidth='100%'
          />
          <SmartDialog
            cancelCallback={() => setShowBalancedDialog(false)}
            minWidth='calc(100vw - 64px)'
            renderComponent={
              <Suspense fallback={<LegacyDialogFallback />}>
                <SensitiveBoundary sensitive={legacyWindowSensitive}>
                  <Balanced
                    preloadWorkflow={balancedWorkflow}
                    startInEditMode
                    hideHeader
                    saveRef={balancedSaveRef}
                    onSaved={() => setShowBalancedDialog(false)}
                  />
                </SensitiveBoundary>
              </Suspense>
            }
            acceptCallback={() => balancedSaveRef.current()}
            accept
            setOpen={showBalancedDialog}
            paperWidth='100%'
          />
          <SmartDialog
            closeCallback={() => setShowDocument(false)}
            accept={false}
            cancel={false}
            close
            disableButtons={isDownloadingFile}
            renderComponent={
              <Suspense fallback={<LegacyDialogFallback />}>
                <SensitiveBoundary sensitive={legacyWindowSensitive}>
                  <Document reference={fabricationOrderRow} resourceType='FABRICATION_ORDER' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />
                </SensitiveBoundary>
              </Suspense>
            }
            setOpen={showDocument}
          />
          {showQr && (
            <QrCodeGeneratorDialog
              open={showQr}
              onClose={() => setShowQr(false)}
              entityType='FabricationOrder'
              entityLabel={t_('Fabrication Order')}
              instance={qrRow}
              subtitle={qrRow?.fabricationOrder}
              availableFields={buildAvailableFields(getColumns(), qrRow, { exclude: ['mainImage'] })}
            />
          )}
          <SmartDialog
            setOpen={confirmationDialogOpen}
            message={t_(`Are you sure you want to ${confirmationAction} the fabrication order?`)}
            title={createDialogTitle({ title: t_('Confirm'), icon: iconToDisplay })}
            accept
            cancel
            close={false}
            acceptCallback={handleConfirmationAccept}
            cancelCallback={() => setConfirmationDialogOpen(false)}
          />
          <PausePartSelectEndJobDialog
            setOpen={unfinishedPartsPauseResume.length === 0 && partsOnDevicesToPause.length > 0 ? partsOnDevicesToPause[0].id : false}
            cancelCallback={() => {
              partsOnDevicesToPause.shift()
              setPartsOnDevicesToPause([...partsOnDevicesToPause])
            }}
            part={partsOnDevicesToPause.length > 0 ? partsOnDevicesToPause[0] : null}
            pauseNowCallback={() => { onPauseWorkflowForce(partsOnDevicesToPause[0].id, false) }}
            pauseAtJobEndCallback={() => { onPauseWorkflowForce(partsOnDevicesToPause[0].id, true) }}
          />
          <PauseResumeFinishedList
            setOpen={unfinishedPartsPauseResume.length > 0}
            parts={unfinishedPartsPauseResume}
            closeCallback={() => setUnfinishedPartsPauseResume([])}
          />
          <SmartDialog
            setOpen={openPartsDialog}
            close
            accept={false}
            cancel={false}
            closeCallback={() => setOpenPartsDialog(false)}
            title={{ icon: <StorageRounded color='primary' />, render: <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>{currentFabricationOrder?.fabricationOrder} - {t_('Parts')}</Typography> }}
            renderComponent={
              <SmartMateriaUITable
                columns={[{
                  name: t_('Code'),
                  field: 'code',
                  edit: false
                }, {
                  name: t_('Status'),
                  field: 'status',
                  edit: false,
                  render: (row) => systemValueLabel(t_, 'PartStatusEnum', row.status)
                }, {
                // S37 (P4/D7): the result as its own datum, painted exactly as Sun's
                // orderwindow.PartMapper publishes it (RN8.1/INV6) -- never derived from status.
                  name: t_('Result'),
                  field: 'result',
                  edit: false,
                  render: (row) => partResultLabel(row.result, t_)
                }]}
                dataFetch={partDialogDataFetch}
                dense
                toolbarDisplay='none'
                pageable
              />
          }
          />
          <FabricationOrderPartProgressDialog open={openProgressDialog} setOpen={setOpenProgressDialog} fabricationOrder={currentFabricationOrder} />
          <SmartDialog
            setOpen={openCopyDialog}
            cancel
            cancelCallback={() => {
              setOpenCopyDialog(false)
            }}
            acceptCallback={() => handleAcceptButtonClick()}
            title={{ icon: <ContentCopyIcon color='primary' />, render: <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>{t_('Fabrication order copy')}</Typography> }}
            renderComponent={
            dialogEditCopy
          }
          />
        </>
      )}
      {/* T7 (S270/S271/RN71): the single entry point into an order's own management — everything
          the row used to offer directly (Modify quantity, Workflow, Balanced, Documents, Copy as,
          Pause/Resume FO, View Associated Parts, FO progress, Generate QR) is a block or a header
          action of this same dialog now (S274/S275: identical whichever list opens it). */}
      {/* Bug #2895 (pasada 16): mounted unconditionally, this dialog only ever saw the state's
          initial `fabricationOrderId` (null) -- `useState(fabricationOrderId)` inside it only
          reads its prop on first mount. Mounting it only while `open` makes every "Open detail"
          click a fresh mount with the real id, which is also what empties `useDetailBlock`'s
          per-block cache on close (RN9) -- the dialog's own design already assumed this. */}
      {detailDialog.open && (
        <FabricationOrderDetailDialog
          fabricationOrderId={detailDialog.fabricationOrderId}
          sensitive={detailDialog.sensitive}
          open={detailDialog.open}
          onClose={(result) => {
            setDetailDialog({ open: false, fabricationOrderId: null, sensitive: false })
            // The detail unmounts with its close, so a notice it wants to give (the order is gone) is shown here.
            if (result?.notice) {
              showAlert(result.notice)
              reloadTable()
            }
          }}
          onOrderChanged={reloadTable}
        />
      )}
      {/* S33-S35 (CU7/RN14,RN15,RN22): "requires confirmation" dialog for the create/edit FO
          form — accept retries with the confirmed delivery date (S34), cancel discards it (S35). */}
      <SmartDialog
        setOpen={deliveryConfirmationDialog.open}
        accept
        cancel
        close={false}
        disableButtons={isSavingFabricationOrder}
        title={createDialogTitle({ color: 'warning', title: t_('Fabrication order delivery date requires confirmation'), icon: WarningAmber })}
        renderComponent={deliveryConfirmationDialog.open ? renderDeliveryConfirmationContent() : ''}
        acceptLabel='Confirm new delivery date'
        acceptCallback={handleDeliveryConfirmationAccept}
        cancelCallback={handleDeliveryConfirmationCancel}
      />
      {/* S119-S122 (CU6/RN44): opens ONLY when Sun answers "requires confirmation" on cancel/close
          (E-M13) — S122 pins that with no dispatched external jobs this never renders and the
          cancel/close flow is exactly the one from before this historia. */}
      <ForcedReturnDialog
        open={forcedReturnGate.dialog.open}
        jobs={forcedReturnGate.dialog.jobs}
        changed={forcedReturnGate.dialog.changed}
        submitting={forcedReturnGate.dialog.submitting}
        onConfirm={handleForcedReturnConfirm}
        onCancel={handleForcedReturnCancel}
      />
      <SmartDialog
        title={{ icon: <DeleteIcon color='primary' />, render: <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>This OF is linked to a part</Typography> }}
        message={t_('Some of the OF(s) have linked parts. Do you want to unlink or delete them?')}
        cancel
        cancelCallback={() => setShowDialog({ ...showDialog, open: false })}
        accept={false}
        close={false}
        setOpen={showDialog.open}
        renderCustomButtons={
          <>
            <Button color='error' onClick={() => { deleteFabricationOrder(false, showDialog.rowValues); setShowDialog(prevValue => { return { ...prevValue, open: false } }) }} variant='outlined'>
              <Typography color='error'>{t_('Unlink')} </Typography>
              <LinkOffIcon color='error' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
            </Button>
            <Button color='error' onClick={() => { deleteFabricationOrder(true, showDialog.rowValues); setShowDialog(prevValue => { return { ...prevValue, open: false } }) }} variant='outlined'>
              <Typography color='error'>{t_('Delete')} </Typography>
              <DeleteIcon color='error' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
            </Button>
          </>
        }
      />

      {AlertElement}
      {/* S32 (CU6/RN21): a blocking overlay from submit to response (create/edit). The backend's
          JIT delivery-feasibility check takes several seconds, so a centered modal spinner makes
          the wait explicit and prevents any further interaction until the response arrives. */}
      <SmartDialog
        setOpen={isSavingFabricationOrder}
        accept={false}
        cancel={false}
        close={false}
        title={createDialogTitle({ color: 'info', title: t_('Saving fabrication order'), icon: HourglassTopOutlined })}
        renderComponent={
          isSavingFabricationOrder
            ? (
              <Stack spacing={2} alignItems='center' sx={{ padding: '1.5em 2.5em' }}>
                <CircularProgress />
                <Typography variant='body2'>{t_('Checking delivery feasibility, please wait')}</Typography>
              </Stack>
              )
            : ''
        }
      />
      {/* Same blocking "Saving" overlay while the fabrication order's WORKFLOW is being saved: saving a
          large workflow takes seconds, and a second Accept meanwhile sent a concurrent, rejected save. */}
      <SmartDialog
        setOpen={isSavingWorkflow}
        accept={false}
        cancel={false}
        close={false}
        title={createDialogTitle({ color: 'info', title: t_('Saving'), icon: HourglassTopOutlined })}
        renderComponent={
          isSavingWorkflow
            ? (
              <Stack data-testid='fo-workflow-saving' spacing={2} alignItems='center' sx={{ padding: '1.5em 2.5em' }}>
                <CircularProgress />
              </Stack>
              )
            : ''
        }
      />
      {/* ⚠️ VENTANA ANTIGUA (E20) — MERGE de develop (H145, #2946). Pertenece a la rama APAGADA, y
          se marca aquí aunque se renderice sin condición: `isLoadingWorkflow` solo lo mueve el
          efecto `getWorkflow` de las nueve acciones de fila, que con el interruptor encendido no
          existe, así que encendido este diálogo queda INERTE y no se ve nunca. Se deja fuera del
          condicional —tal cual lo mezcló git— porque envolverlo no cambiaría nada visible y sí
          tocaría estructura de develop sin motivo; lo que sí hace falta es la MARCA, para que el
          día que se retire el interruptor esto se borre con lo demás en vez de quedarse huérfano
          apuntando a un estado que ya no mueve nadie.
          H145 (RN1/RN2/D6/D7/DI1): indicador bloqueante mientras carga el workflow de una OF —
          misma forma que el de "Guardando orden de fabricación" de arriba, siempre visible (sin
          umbral antiparpadeo, D7) y sin forma de cancelarlo (D6). */}
      <SmartDialog
        setOpen={isLoadingWorkflow}
        accept={false}
        cancel={false}
        close={false}
        title={createDialogTitle({ color: 'info', title: t_('Loading workflow'), icon: HourglassTopOutlined })}
        renderComponent={
          isLoadingWorkflow
            ? (
              <Stack spacing={2} alignItems='center' sx={{ padding: '1.5em 2.5em' }}>
                <CircularProgress />
              </Stack>
              )
            : ''
        }
      />
      {/*
        S69/S70: the resync dialog of ONE order. It is mounted only while a row is being
        resynchronised, so the window carries no dialog of a feature nobody asked for.
      */}
      {resyncRow?.id !== undefined && (
        <FabricationOrderResyncAction
          row={resyncRow}
          nodes={clusterTargetNodes}
          showAlert={showAlert}
          onClose={() => setResyncRow({})}
          onResynced={toogleResetMultipleSelectionValue}
        />
      )}
      <SmartMateriaUITable
        tableTuneUniqueKey='fabricationOrders'
        rowPlaceholder={protectedRowPlaceholder}
        disableSelectAllData={visionCoversSensitive}
        columns={getColumns()}
        title={allFoMode ? t_('Fabrication orders') : lineOrder.lineNumber}
        dataFetch={dataFetch}
        forceLoad={tableReloadSignal}
        actions={actions}
        multipleSelectionActions={multipleSelectionActions}
        multipleSelection
        sortOptions={sortOptions}
        pageable
        preselectedRecord={allFoMode ? parentPreselectedRecordFrom(repositorySelection) : null}
        addModeByDefault={addModeByDefault}
        resetMultipleSelectionValue={resetMultipleSelectionValue}
        validator={validator}
        editingRow={(event) => setEditing(event)}
        onEditIndexChanged={(editIndex, editingRow) => { setIsAddingNewRow(editIndex === -1); setEditingItem(editingRow) }}
        toolbar={toolbar}
        forceDisableSaveButton={isSavingFabricationOrder}
        resetPage={allValue}
        // H179 (S98): mostrar u ocultar las OF sin workflow vuelve a la primera página, porque la página N del conjunto
        // anterior puede no existir en el nuevo. En una tabla `pageable` eso lo hace `resetPageable`: `resetPage` solo
        // reinicia la página de las tablas NO paginadas.
        resetPageable={showWithoutWorkflow}
        importFile={importViaConnector
          ? undefined
          : [{
              bean: {
                beanName: 'FabricationOrderImportDto',
                beanPackage: 'com.smartpm.sun.usecase.fabricationorderimport.infrastructure.rest',
                serviceName: 'FabricationOrderImportService',
                servicePackage: 'com.smartpm.sun.usecase.fabricationorderimport.application'
              },
              name: t_('Fabrication Order')
            }]}
        importFileDisabled={!isUserAuthorized('IMPORT_FILE_FABRICATION_ORDERS')}
      />
      {importViaConnector && (
        <ImportConnectorDialog
          open={importConnectorOpen}
          onClose={(imported) => {
            setImportConnectorOpen(false)
            if (imported) reloadTable()
          }}
        />
      )}

    </>
  )
}

export default FabricationOrder
